import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { TransactionDetailModalComponent, GlTransactionDetailData } from '../../shared/transaction-detail-modal/transaction-detail-modal.component';
import { PostedFilterMode } from '../../constants/posted-filter-options';

export interface AccountDetailModalData {
	accountcode: string;
	accountname: string;
	periodno?: number | string;
	periodLabel: string;
	fromDate?: string;
	toDate?: string;
	posted?: PostedFilterMode;
}

/** Emitted when closing so callers can refresh lists (e.g. Simple Trial Balance tag breakdown). */
export interface AccountDetailModalCloseResult {
	dataChanged?: boolean;
}

interface GlTransaction {
	type: string;
	typeid?: number;
	number: string;
	trandate: string;
	debit: string;
	credit: string;
	check: string;
	narrative: string;
	balance: string;
	tag: string;
	counterindex: number;
	tagref: number | null;
	debtorno?: string | null;
	debtorname?: string | null;
	/** Server: prior-period unposted bridge row — not a drill-down GL line. */
	adjustment_only?: number | boolean;
}

@Component({
	selector: 'app-account-detail-modal',
	templateUrl: './account-detail-modal.component.html',
	styleUrls: ['./account-detail-modal.component.scss']
})
export class AccountDetailModalComponent implements OnInit {
	/**
	 * Set when a transaction line save succeeds. Read from `MatDialogRef.beforeClosed` (backdrop/Esc
	 * do not go through `close()`, so the close result alone is not enough).
	 */
	parentShouldRefreshLists = false;

	loading = false;
	transactions: GlTransaction[] = [];
	endbalance: number | null = null;
	periodtotal: number | null = null;
	/** Period opening (chartdetails.bfwd) from API — P&L and B/S use this for running balance. */
	openingBalance: number | null = null;
	chartdetails: { bfwd: number; actual: number; balance: number } | null = null;
	/** Earliest fiscal period no. (for “show unposted” deep-link). */
	private earliestPeriodNo: number | null = null;

	constructor(
		public dialogRef: MatDialogRef<AccountDetailModalComponent, AccountDetailModalCloseResult>,
		@Inject(MAT_DIALOG_DATA) public data: AccountDetailModalData,
		private generalLedgerService: GeneralLedgerService,
		private dialog: MatDialog,
		private router: Router
	) {}

	ngOnInit(): void {
		this.generalLedgerService.getPeriods().subscribe((r: any) => {
			const per = r?.periods || [];
			if (per.length) {
				this.earliestPeriodNo = Math.min(...per.map((p: { periodno: string | number }) => Number(p.periodno)));
			}
		});
		this.load();
	}

	load(): void {
		this.loading = true;
		const payload = this.data.fromDate && this.data.toDate
			? {
				glaccount: this.data.accountcode,
				from_date: this.data.fromDate,
				to_date: this.data.toDate,
				gl_tag: '',
				posted: this.data.posted || 'all'
			}
			: {
				glaccount: this.data.accountcode,
				from_period: this.data.periodno,
				to_period: this.data.periodno,
				gl_tag: '',
				posted: this.data.posted || 'all'
			};
		this.generalLedgerService.getAccountInquiry(payload).subscribe({
			next: (results: any) => {
				const period = results?.periods?.[0];
				this.transactions = period?.transactions || [];
				this.endbalance = parseFloat(results?.endbalance) || 0;
				this.periodtotal = parseFloat(period?.periodtotal) || 0;
				const sb = period?.startbalance;
				this.openingBalance = sb !== undefined && sb !== null && sb !== '' ? parseFloat(String(sb).replace(/,/g, '')) : null;
				if (this.openingBalance !== null && Number.isNaN(this.openingBalance)) {
					this.openingBalance = null;
				}
				const cd = results?.chartdetails;
				this.chartdetails = cd ? {
					bfwd:    parseFloat(cd.bfwd)    || 0,
					actual:  parseFloat(cd.actual)  || 0,
					balance: parseFloat(cd.balance) || 0,
				} : null;
				this.loading = false;
			},
			error: () => {
				this.transactions = [];
				this.loading = false;
			}
		});
	}

	isAdjustmentRow(trans: GlTransaction): boolean {
		return trans.adjustment_only === 1 || trans.adjustment_only === true;
	}

	onTransactionRowClick(trans: GlTransaction): void {
		if (this.isAdjustmentRow(trans)) {
			return;
		}
		this.openDetail(trans);
	}

	openDetail(trans: GlTransaction): void {
		const detail: GlTransactionDetailData = {
			type: trans.type,
			typeid: trans.typeid,
			number: trans.number,
			trandate: trans.trandate,
			debit: parseFloat(trans.debit) || 0,
			credit: parseFloat(trans.credit) || 0,
			chequeno: trans.check,
			narrative: trans.narrative,
			balance: parseFloat(trans.balance) || 0,
			tag: trans.tag,
			periodno: this.data.periodno,
			periodname: this.data.periodLabel,
			counterindex: trans.counterindex,
			tagref: trans.tagref ?? null,
			debtorno: trans.debtorno ?? null,
			debtorname: trans.debtorname ?? null
		};
		this.dialog.open(TransactionDetailModalComponent, {
			width: '1100px',
			maxWidth: '94vw',
			data: detail
		}).afterClosed().subscribe((result: any) => {
			if (result?.saved) {
				this.parentShouldRefreshLists = true;
				this.load();
			}
		});
	}

	close(): void {
		this.dialogRef.close({ dataChanged: this.parentShouldRefreshLists });
	}

	showOpeningBalanceRow(): boolean {
		return this.openingBalance != null && Math.abs(this.openingBalance) > 0.0005;
	}

	/** Posted=all bridge row: prior-period unposted not listed in this single-period view. */
	showUnpostedFinder(): boolean {
		return ((this.data.posted || 'all') as string) === 'all' && this.transactions.some(t => this.isAdjustmentRow(t));
	}

	openAccountInquiryUnposted(): void {
		const toP = Number(this.data.periodno);
		const fromP = this.earliestPeriodNo != null ? this.earliestPeriodNo : toP;
		this.dialogRef.close({ dataChanged: this.parentShouldRefreshLists });
		this.router.navigate(['/general-ledger/account-inquiry'], {
			queryParams: {
				accountcode: this.data.accountcode,
				from_period: String(fromP),
				to_period: String(toP),
				posted: 'unposted'
			}
		});
	}
}
